import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import AdModal from '../../components/feature/AdModal';

interface Match {
  id: string;
  full_name: string;
  photos: string[];
  last_message?: string;
  last_message_time?: string;
  unread_count: number;
  is_online: boolean;
}

interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  created_at: string;
  is_read: boolean;
  translated_content?: string;
}

const MessagesPage = () => {
  const { user, profile } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAd, setShowAd] = useState(false);
  const [hasViewedAd, setHasViewedAd] = useState(false);
  const [showTranslate, setShowTranslate] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [targetLang, setTargetLang] = useState('tr');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageSubscription = useRef<any>(null);

  useEffect(() => {
    if (!user) return;
    loadMatches();
  }, [user]);

  useEffect(() => {
    if (selectedMatch && user) {
      loadMessages();
      markMessagesAsRead();
      subscribeToMessages();
    }

    return () => {
      if (messageSubscription.current) {
        messageSubscription.current.unsubscribe();
      }
    };
  }, [selectedMatch, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMatches = async () => {
    if (!user) return;

    const { data: matchData } = await supabase
      .from('matches')
      .select('user1_id, user2_id')
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

    if (!matchData) {
      setLoading(false);
      return;
    }

    const matchUserIds = matchData.map(m => 
      m.user1_id === user.id ? m.user2_id : m.user1_id
    );

    if (matchUserIds.length === 0) {
      setLoading(false);
      return;
    }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('*')
      .in('id', matchUserIds);

    if (!profiles) {
      setLoading(false);
      return;
    }

    const matchesWithMessages = await Promise.all(
      profiles.map(async (profile) => {
        const { data: lastMessage } = await supabase
          .from('messages')
          .select('content, created_at')
          .or(`and(sender_id.eq.${user.id},receiver_id.eq.${profile.id}),and(sender_id.eq.${profile.id},receiver_id.eq.${user.id})`)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        const { count } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('sender_id', profile.id)
          .eq('receiver_id', user.id)
          .eq('is_read', false);

        return {
          id: profile.id,
          full_name: profile.full_name,
          photos: profile.photos || [],
          last_message: lastMessage?.content,
          last_message_time: lastMessage?.created_at,
          unread_count: count || 0,
          is_online: profile.is_online || false,
        };
      })
    );

    matchesWithMessages.sort((a, b) => {
      if (!a.last_message_time) return 1;
      if (!b.last_message_time) return -1;
      return new Date(b.last_message_time).getTime() - new Date(a.last_message_time).getTime();
    });

    setMatches(matchesWithMessages);
    setLoading(false);
  };

  const loadMessages = async () => {
    if (!user || !selectedMatch) return;

    const { data } = await supabase
      .from('messages')
      .select('*')
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${selectedMatch.id}),and(sender_id.eq.${selectedMatch.id},receiver_id.eq.${user.id})`)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data);

      // Check if user has viewed ad for this match
      const { data: adView } = await supabase
        .from('ad_views')
        .select('*')
        .eq('user_id', user.id)
        .eq('match_id', selectedMatch.id)
        .single();

      if (!adView && data.length > 0) {
        setShowAd(true);
      } else {
        setHasViewedAd(true);
      }
    }
  };

  const subscribeToMessages = () => {
    if (!user || !selectedMatch) return;

    messageSubscription.current = supabase
      .channel('messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.new.sender_id === selectedMatch.id) {
            setMessages(prev => [...prev, payload.new as Message]);
            markMessagesAsRead();
          }
        }
      )
      .subscribe();
  };

  const markMessagesAsRead = async () => {
    if (!user || !selectedMatch) return;

    await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('sender_id', selectedMatch.id)
      .eq('receiver_id', user.id)
      .eq('is_read', false);

    loadMatches();
  };

  const handleAdComplete = async () => {
    if (!user || !selectedMatch) return;

    await supabase
      .from('ad_views')
      .insert({
        user_id: user.id,
        match_id: selectedMatch.id,
      });

    setShowAd(false);
    setHasViewedAd(true);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !selectedMatch || !newMessage.trim()) return;

    const { error } = await supabase
      .from('messages')
      .insert({
        sender_id: user.id,
        receiver_id: selectedMatch.id,
        content: newMessage.trim(),
      });

    if (!error) {
      setNewMessage('');
      loadMessages();
    }
  };

  const handleTranslateMessages = async () => {
    if (!messages.length || translating) return;

    setTranslating(true);

    try {
      const translatedMessages = await Promise.all(
        messages.map(async (msg) => {
          if (msg.sender_id === user?.id) return msg;

          const response = await fetch(
            `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/translate-message`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                text: msg.content,
                targetLang: targetLang,
                sourceLang: 'auto',
              }),
            }
          );

          if (response.ok) {
            const data = await response.json();
            return { ...msg, translated_content: data.translatedText };
          }

          return msg;
        })
      );

      setMessages(translatedMessages);
    } catch (error) {
      console.error('Translation error:', error);
    }

    setTranslating(false);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 24) {
      return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Navbar />
        <div className="text-center pt-20">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Giriş Yapmanız Gerekiyor</h2>
          <p className="text-gray-600 mb-6">Mesajlarınızı görüntülemek için lütfen giriş yapın</p>
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/kayit')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Giriş Yap / Kayıt Ol
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      <Navbar />
      
      <AdModal isOpen={showAd} onClose={handleAdComplete} adType="message_read" />

      <div className="pt-20 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-display font-bold text-gray-900 mb-6">Mesajlar</h1>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
            <div className="flex h-full">
              {/* Matches List */}
              <div className="w-80 border-r border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Eşleşmeler</h2>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-gray-500">Yükleniyor...</div>
                    </div>
                  ) : matches.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                      <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-4">
                        <i className="ri-message-3-line text-3xl text-gray-400"></i>
                      </div>
                      <p className="text-gray-600 mb-4">Henüz eşleşmeniz yok</p>
                      <button
                        onClick={() => window.REACT_APP_NAVIGATE('/ara')}
                        className="px-4 py-2 bg-primary-600 text-white text-sm rounded-full hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        Keşfet
                      </button>
                    </div>
                  ) : (
                    matches.map((match) => (
                      <div
                        key={match.id}
                        onClick={() => setSelectedMatch(match)}
                        className={`flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                          selectedMatch?.id === match.id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <div className="relative">
                          <div className="w-14 h-14">
                            <img
                              src={match.photos[0] || 'https://public.readdy.ai/ai/img_res/bf720c163872af04359773faf749f27e.jpg'}
                              alt={match.full_name}
                              className="w-full h-full object-cover object-top rounded-full"
                            />
                          </div>
                          {match.is_online && (
                            <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-1">
                            <h3 className="font-semibold text-gray-900 truncate">{match.full_name}</h3>
                            {match.last_message_time && (
                              <span className="text-xs text-gray-500 ml-2">
                                {formatTime(match.last_message_time)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 truncate">{match.last_message || 'Henüz mesaj yok'}</p>
                        </div>
                        {match.unread_count > 0 && (
                          <div className="w-6 h-6 flex items-center justify-center bg-primary-600 text-white text-xs font-bold rounded-full">
                            {match.unread_count}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className="flex-1 flex flex-col">
                {selectedMatch ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12">
                          <img
                            src={selectedMatch.photos[0] || 'https://readdy.ai/api/search-image?query=A%20beautiful%20person%20with%20elegant%20style%20and%20warm%20smile%20in%20a%20modern%20setting%20with%20soft%20natural%20lighting%20and%20professional%20portrait%20photography%20aesthetic%20featuring%20simple%20clean%20background&width=100&height=100&seq=default2&orientation=squarish'}
                            alt={selectedMatch.full_name}
                            className="w-full h-full object-cover object-top rounded-full"
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{selectedMatch.full_name}</h3>
                          <p className="text-xs text-gray-500">
                            {selectedMatch.is_online ? 'Çevrimiçi' : 'Çevrimdışı'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowTranslate(!showTranslate)}
                          className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                          title="Çeviri"
                        >
                          <i className="ri-translate text-xl"></i>
                        </button>
                        <button
                          onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                          className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                          title="Sesli Arama"
                        >
                          <i className="ri-phone-line text-xl"></i>
                        </button>
                      </div>
                    </div>

                    {/* Translation Bar */}
                    {showTranslate && (
                      <div className="p-3 bg-primary-50 border-b border-primary-100 flex items-center gap-3">
                        <select
                          value={targetLang}
                          onChange={(e) => setTargetLang(e.target.value)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="tr">Türkçe</option>
                          <option value="en">English</option>
                          <option value="es">Español</option>
                          <option value="fr">Français</option>
                          <option value="de">Deutsch</option>
                          <option value="it">Italiano</option>
                          <option value="pt">Português</option>
                          <option value="ru">Русский</option>
                          <option value="ar">العربية</option>
                          <option value="zh">中文</option>
                          <option value="ja">日本語</option>
                          <option value="ko">한국어</option>
                        </select>
                        <button
                          onClick={handleTranslateMessages}
                          disabled={translating}
                          className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                        >
                          {translating ? 'Çevriliyor...' : 'Mesajları Çevir'}
                        </button>
                      </div>
                    )}

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                      {messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="text-center">
                            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                              <i className="ri-chat-3-line text-3xl text-gray-400"></i>
                            </div>
                            <p className="text-gray-600">Henüz mesaj yok</p>
                            <p className="text-sm text-gray-500 mt-1">İlk mesajı gönderin!</p>
                          </div>
                        </div>
                      ) : (
                        messages.map((message) => {
                          const isOwn = message.sender_id === user.id;
                          return (
                            <div
                              key={message.id}
                              className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                                  isOwn
                                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                                    : 'bg-gray-100 text-gray-900'
                                }`}
                              >
                                <p className="text-sm">{message.translated_content || message.content}</p>
                                {message.translated_content && (
                                  <p className="text-xs opacity-70 mt-1 italic">{message.content}</p>
                                )}
                                <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                                  {formatTime(message.created_at)}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Mesajınızı yazın..."
                          className="flex-1 px-4 py-3 text-sm border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={!hasViewedAd}
                        />
                        <button
                          type="submit"
                          disabled={!newMessage.trim() || !hasViewedAd}
                          className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full hover:from-pink-600 hover:to-purple-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-send-plane-fill text-xl"></i>
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                        <i className="ri-chat-smile-3-line text-4xl text-gray-400"></i>
                      </div>
                      <p className="text-gray-600">Bir sohbet seçin</p>
                      <p className="text-sm text-gray-500 mt-1">Mesajlaşmaya başlamak için bir eşleşme seçin</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default MessagesPage;
